import { Box, CircularProgress, Alert, Chip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useUsers } from '@lingaai/shared-hooks';

interface UsersTabProps {
  tenantId: string;
}

export function UsersTab({ tenantId }: UsersTabProps) {
  const { data: users, isLoading } = useUsers(tenantId);

  const columns: GridColDef[] = [
    {
      field: 'full_name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
      valueFormatter: (value) => value || '-',
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const roleColors: Record<string, 'primary' | 'success' | 'default'> = {
          super_admin: 'default',
          admin: 'primary',
          portco: 'success',
        };
        return <Chip label={params.value} size="small" color={roleColors[params.value as string] || 'default'} />;
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      valueFormatter: (value) => {
        return new Date(value).toLocaleDateString();
      },
    },
  ];

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!users || users.length === 0) {
    return <Alert severity="info">No users found for this tenant.</Alert>;
  }

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <DataGrid
        rows={users}
        columns={columns}
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 },
          },
        }}
      />
    </Box>
  );
}
